import { Badge, type BadgeProps } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import type { ContractStatus, ExtractionStatus, LicenseComplianceStatus } from '@/types';

const contractStatusVariant: Record<ContractStatus, BadgeProps['variant']> = {
  Active: 'success',
  Expired: 'destructive',
  PendingRenewal: 'warning',
  Terminated: 'default',
  Draft: 'info',
};

const complianceStatusVariant: Record<LicenseComplianceStatus, BadgeProps['variant']> = {
  Compliant: 'success',
  Warning: 'warning',
  NonCompliant: 'destructive',
  Unknown: 'default',
};

export function ContractStatusBadge({ status }: { status: ContractStatus }) {
  return <Badge variant={contractStatusVariant[status]}>{status}</Badge>;
}

export function ActiveStatusBadge({ isActive }: { isActive: boolean }) {
  return <Badge variant={isActive ? 'success' : 'default'}>{isActive ? 'Active' : 'Inactive'}</Badge>;
}

export function ComplianceStatusBadge({ status }: { status: LicenseComplianceStatus }) {
  return <Badge variant={complianceStatusVariant[status]}>{status}</Badge>;
}

/**
 * Badge label and colour per extraction status (spec 5.2). Superseded is never a live document's
 * latest extraction in practice and shows nothing.
 */
const extractionStatusBadge: Record<ExtractionStatus, { label: string; variant: BadgeProps['variant'] } | null> = {
  Pending: { label: 'Extracting', variant: 'info' },
  Processing: { label: 'Extracting', variant: 'info' },
  Review: { label: 'Needs review', variant: 'warning' },
  Applied: { label: 'Applied', variant: 'success' },
  Rejected: { label: 'Rejected', variant: 'default' },
  Failed: { label: 'Failed', variant: 'destructive' },
  Superseded: null,
};

/**
 * The extraction badge on a document row: the latest extraction's status, "Not extractable" for
 * a file type that cannot be sent, nothing when the document has no extraction. A failure shows
 * its message as a tooltip on a focusable button (Radix links it with aria-describedby).
 */
export function ExtractionStatusBadge({
  status,
  isExtractable,
  error,
}: {
  status?: ExtractionStatus;
  isExtractable?: boolean;
  error?: string;
}) {
  if (!status) {
    // Only an explicit false: an API older than this frontend sends no flag at all.
    return isExtractable === false ? <Badge variant="outline">Not extractable</Badge> : null;
  }

  const badge = extractionStatusBadge[status];
  if (!badge) {
    return null;
  }

  if (status === 'Failed' && error) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <button type="button" className="rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
            <Badge variant={badge.variant}>{badge.label}</Badge>
          </button>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs">{error}</TooltipContent>
      </Tooltip>
    );
  }

  return <Badge variant={badge.variant}>{badge.label}</Badge>;
}
